import React, { useEffect, useState, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getUserAvatarColor } from "../../../utils/kpiColors";

const MonthYearCustomInput = forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 rounded-xl text-sm font-semibold text-slate-800 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all shadow-2xs cursor-pointer"
  >
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-slate-500"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
    <span>{value || "Select Month & Year"}</span>
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-slate-400 ml-0.5"
    >
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  </button>
));

const getHeatmapColorStyle = (receivedScore, targetScore = 100) => {
  const received = Math.max(0, Number(receivedScore || 0));
  const target = Number(targetScore || 0) > 0 ? Number(targetScore) : 100;

  // Percentage calculation (0% to 100%) based on received points vs target points
  const ratio = Math.min(1.0, Math.max(0, received / target));

  // Opacity: 100% means color #1a1a18 as original (alpha 1.0), 0% means fully transparent (alpha 0.0)
  const alpha = ratio.toFixed(2);

  // Dynamic text color for readability: white text when opacity >= 0.45, dark charcoal (#1a1a18) when light/transparent
  const textColor = ratio >= 0.45 ? '#ffffff' : '#1a1a18';

  return {
    backgroundColor: `rgba(26, 26, 24, ${alpha})`,
    color: textColor,
    boxShadow: ratio < 0.15 ? 'inset 0 0 0 1px #e2e8f0' : 'none',
  };
};

const KPIAssessments = () => {
  const dispatch = useDispatch();
  const { adminDashboard, teamReview, loading } = useSelector((state) => state.kpi);

  const now = new Date();
  const currentActualMonth = now.getMonth() + 1; // 1 to 12
  const currentActualYear = now.getFullYear();

  // Selected filter state (default to current month and year as numbers)
  const [selectedMonth, setSelectedMonth] = useState(currentActualMonth);
  const [selectedYear, setSelectedYear] = useState(currentActualYear);

  useEffect(() => {
    const params = {
      month: Number(selectedMonth),
      year: Number(selectedYear)
    };
    dispatch.kpi.getAdminDashboard(params);
    dispatch.kpi.getTeamReview(params);
  }, [dispatch, selectedMonth, selectedYear]);

  const summary = adminDashboard?.summary || {};
  const teamMetrics = teamReview || {};

  const teamAvgKpiObj = summary?.team_avg_kpi || {};
  const efficiencyObj = summary?.efficiency_score || {};
  const deliveryObj = summary?.delivery_score || {};
  const engagementObj = summary?.engagement_score || {};

  const teamAvgVal = teamAvgKpiObj.value !== undefined ? teamAvgKpiObj.value : (teamMetrics.team_avg_kpi || 0);
  const teamAvgChange = teamAvgKpiObj.change;
  const teamAvgTrend = teamAvgKpiObj.trend;

  const effVal = efficiencyObj.value !== undefined ? efficiencyObj.value : (teamMetrics.efficiency || 0);
  const effChange = efficiencyObj.change;
  const effTrend = efficiencyObj.trend;

  const delVal = deliveryObj.value !== undefined ? deliveryObj.value : (teamMetrics.delivery || 0);
  const delChange = deliveryObj.change;
  const delTrend = deliveryObj.trend;

  const engVal = engagementObj.value !== undefined ? engagementObj.value : (teamMetrics.engagement || 0);
  const engChange = engagementObj.change;
  const engTrend = engagementObj.trend;

  // Helper to extract clean full name with email fallback
  const getLeaderFullName = (leader = {}) => {
    if (leader.full_name && leader.full_name.trim()) return leader.full_name.trim();
    const first = leader.first_name || "";
    const last = leader.last_name || "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;
    if (leader.name && leader.name.trim()) return leader.name.trim();
    if (leader.email && leader.email.includes("@")) {
      const username = leader.email.split("@")[0];
      return username
        .replace(/[._-]/g, " ")
        .split(" ")
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ")
        .replace(/\s+tblr$/i, "")
        .trim();
    }
    return "Team Member";
  };

  // Leaderboard raw list from adminDashboard.leaderboard OR teamReview.ranking
  const rawLeaderboardList = (Array.isArray(adminDashboard?.leaderboard) && adminDashboard.leaderboard.length > 0)
    ? adminDashboard.leaderboard
    : (teamReview?.ranking || []);

  // Sorted leaderboard list by rank & total KPI points earned
  const leaderboardList = React.useMemo(() => {
    const list = [...rawLeaderboardList];
    return list.sort((a, b) => {
      const rA = a.rank !== undefined && a.rank !== null ? Number(a.rank) : 9999;
      const rB = b.rank !== undefined && b.rank !== null ? Number(b.rank) : 9999;
      if (rA !== rB) return rA - rB;

      const pA = a.total_points !== undefined ? Number(a.total_points) : (a.total_score !== undefined ? Number(a.total_score) : (a.monthly_score !== undefined ? Number(a.monthly_score) : (a.net_points || 0)));
      const pB = b.total_points !== undefined ? Number(b.total_points) : (b.total_score !== undefined ? Number(b.total_score) : (b.monthly_score !== undefined ? Number(b.monthly_score) : (b.net_points || 0)));
      return pB - pA;
    });
  }, [rawLeaderboardList]);

  // Parse Heatmap data strictly from adminDashboard.heatmap
  const rawHeatmap = Array.isArray(adminDashboard?.heatmap) ? adminDashboard.heatmap : [];

  const monthHeaders = (rawHeatmap[0]?.monthly_scores || []).map(m => m.month);
  const defaultMonths = ['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug'];
  const displayMonths = monthHeaders.length > 0 ? monthHeaders : defaultMonths;

  const heatmapRows = rawHeatmap.length > 0 ? rawHeatmap.map((item, idx) => {
    const userObj = item.user || {};
    const name = getLeaderFullName(userObj);
    const nameParts = name.trim().split(" ").filter(Boolean);
    const init = nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
      : (name.slice(0, 2) || "TM").toUpperCase();
    
    const monthlyScores = item.monthly_scores || item.scores || item.data || [];
    const avatarColor = getUserAvatarColor(userObj._id || userObj.email || name, idx);
    
    return {
      id: userObj._id || idx,
      name,
      designation: userObj.designation || userObj.designation_details?.name || "",
      init,
      color: avatarColor,
      totalScore: item.total_score || 0,
      isCurrentUser: Boolean(item.is_current_user),
      monthlyScores
    };
  }) : [];

  return (
    <div className="w-full">
      <style>{`
        .kpi-month-picker.react-datepicker {
          font-family: inherit !important;
          border-radius: 1.25rem !important;
          border: 1px solid #e2e8f0 !important;
          box-shadow: 0 20px 30px -10px rgba(15, 23, 42, 0.15), 0 10px 15px -5px rgba(15, 23, 42, 0.08) !important;
          overflow: hidden !important;
          background-color: #ffffff !important;
          padding: 1rem !important;
          width: 320px !important;
          box-sizing: border-box !important;
        }
        .kpi-month-picker .react-datepicker__header,
        .kpi-month-picker .react-datepicker__header--custom,
        .kpi-month-picker .react-datepicker-year-header {
          background-color: #ffffff !important;
          border-bottom: 1px solid #f1f5f9 !important;
          padding-top: 0.25rem !important;
          padding-bottom: 0.75rem !important;
          margin-bottom: 0.75rem !important;
          position: relative !important;
        }
        .kpi-month-picker .react-datepicker__current-month,
        .kpi-month-picker .react-datepicker-year-header,
        .kpi-month-picker .react-datepicker__year-text {
          font-size: 1rem !important;
          font-weight: 800 !important;
          color: #0f172a !important;
          letter-spacing: -0.01em !important;
        }
        .kpi-month-picker .react-datepicker__navigation {
          top: 0.75rem !important;
        }
        .kpi-month-picker .react-datepicker__month-container {
          float: none !important;
          width: 100% !important;
        }
        .kpi-month-picker .react-datepicker__month {
          margin: 0 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 0.5rem !important;
          width: 100% !important;
        }
        .kpi-month-picker .react-datepicker__month-wrapper {
          display: flex !important;
          flex-direction: row !important;
          flex-wrap: nowrap !important;
          justify-content: space-between !important;
          align-items: center !important;
          margin: 0 !important;
          width: 100% !important;
          gap: 0.5rem !important;
        }
        .kpi-month-picker .react-datepicker__month-text,
        .kpi-month-picker .react-datepicker__quarter-text {
          display: inline-flex !important;
          flex: 1 1 0% !important;
          align-items: center !important;
          justify-content: center !important;
          height: 40px !important;
          min-width: 0 !important;
          margin: 0 !important;
          padding: 0 4px !important;
          border-radius: 0.75rem !important;
          font-size: 0.8125rem !important;
          font-weight: 600 !important;
          color: #334155 !important;
          background-color: transparent !important;
          transition: all 0.15s ease !important;
          cursor: pointer !important;
          box-sizing: border-box !important;
        }
        .kpi-month-picker .react-datepicker__month-text:hover,
        .kpi-month-picker .react-datepicker__quarter-text:hover {
          background-color: #f1f5f9 !important;
          color: #0f172a !important;
        }
        .kpi-month-picker .react-datepicker__month-text--selected,
        .kpi-month-picker .react-datepicker__month-text--selected:hover,
        .kpi-month-picker .react-datepicker__month-text--keyboard-selected,
        .kpi-month-picker .react-datepicker__month-text--keyboard-selected:hover,
        .kpi-month-picker .react-datepicker__month-option--selected {
          background-color: #0f172a !important;
          color: #ffffff !important;
          font-weight: 700 !important;
        }
        .kpi-month-picker .react-datepicker__month-text--selected *,
        .kpi-month-picker .react-datepicker__month-text--keyboard-selected * {
          color: #ffffff !important;
        }
        .kpi-month-picker .react-datepicker__month-text--disabled {
          color: #cbd5e1 !important;
          cursor: not-allowed !important;
          pointer-events: none !important;
          background-color: transparent !important;
          opacity: 0.4 !important;
        }
        .kpi-month-picker .react-datepicker__navigation--disabled {
          display: none !important;
        }
        .kpi-month-picker .react-datepicker__month-text--today:not(.react-datepicker__month-text--selected) {
          border: 1.5px solid #0f172a !important;
          font-weight: 700 !important;
          color: #0f172a !important;
        }
      `}</style>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Assessments</h1>
          <p className="text-slate-500 text-sm">
            Compare leaders, departments, and KPI categories
          </p>
        </div>
        <div className="relative">
          <DatePicker
            selected={new Date(selectedYear, selectedMonth - 1, 1)}
            onChange={(date) => {
              if (date) {
                setSelectedMonth(date.getMonth() + 1);
                setSelectedYear(date.getFullYear());
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            maxDate={new Date()}
            calendarClassName="kpi-month-picker"
            customInput={<MonthYearCustomInput />}
            popperPlacement="bottom-end"
          />
        </div>
      </div>

      {/* Summary Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Team Avg KPI</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-3xl font-light text-slate-900">{teamAvgVal}</div>
            {teamAvgChange !== undefined && teamAvgChange !== null && (
              <div className={`text-xs font-semibold ${teamAvgTrend === 'DOWN' ? 'text-rose-500' : 'text-emerald-500'}`}>
                {teamAvgTrend === 'DOWN' ? '↓' : '↑'} {teamAvgChange}%
              </div>
            )}
          </div>
          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, (teamAvgVal / 250) * 100))}%` }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Efficiency Score</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-3xl font-light text-slate-900">{effVal}%</div>
            {effChange !== undefined && effChange !== null && (
              <div className={`text-xs font-semibold ${effTrend === 'DOWN' ? 'text-rose-500' : 'text-indigo-500'}`}>
                {effTrend === 'DOWN' ? '↓' : '↑'} {effChange}%
              </div>
            )}
          </div>
          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, effVal))}%` }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Delivery Score</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-3xl font-light text-slate-900">{delVal}%</div>
            {delChange !== undefined && delChange !== null && (
              <div className={`text-xs font-semibold ${delTrend === 'DOWN' ? 'text-rose-500' : 'text-blue-600'}`}>
                {delTrend === 'DOWN' ? '↓' : '↑'} {delChange}%
              </div>
            )}
          </div>
          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, Math.max(0, delVal))}%` }}></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Engagement Score</div>
          <div className="flex items-baseline gap-2 mb-3">
            <div className="text-3xl font-light text-slate-900">{engVal}%</div>
            {engChange !== undefined && engChange !== null && (
              <div className={`text-xs font-semibold ${engTrend === 'DOWN' ? 'text-rose-500' : 'text-amber-600'}`}>
                {engTrend === 'DOWN' ? '↓' : '↑'} {engChange}%
              </div>
            )}
          </div>
          <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, engVal))}%` }}></div>
          </div>
        </div>
      </div>

      {/* Leader Ranking Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
        <div className="px-6 py-5 border-b border-slate-100 ">
          <h2 className="text-base font-semibold text-slate-900">Leader ranking</h2>
          <p className="text-sm text-slate-500 mt-0.5">Sorted by total KPI points earned</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-[#f7f6f3]">
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Rank</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Leader</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider">Categories</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-32">Efficiency</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider w-32">Delivery</th>
                <th className="px-6 py-3.5 text-[11px] font-bold text-slate-700 uppercase tracking-wider text-right">KPI Score</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leaderboardList.length > 0 ? (
                leaderboardList.map((row, i) => {
                  const rankNum = row.rank !== undefined ? row.rank : (i + 1);
                  const fullName = getLeaderFullName(row);
                  const nameParts = fullName.split(" ").filter(Boolean);
                  const initials = nameParts.length >= 2
                    ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
                    : (fullName.slice(0, 2) || "TM").toUpperCase();

                  const role = row.designation || row.designation_details?.name || row.role || "Team Member";
                  const effPct = row.efficiency !== undefined ? row.efficiency : (row.efficiency_score !== undefined ? row.efficiency_score : 0);
                  const delPct = row.delivery !== undefined ? row.delivery : (row.delivery_score !== undefined ? row.delivery_score : 0);
                  const kpiScore = row.monthly_score !== undefined ? row.monthly_score : 0;

                  const allCats = [
                    ...(Array.isArray(row.categories) ? row.categories : []),
                    ...(Array.isArray(row.kpi_categories) ? row.kpi_categories : []),
                  ];

                  const avatarColorClass = getUserAvatarColor(row.user_id || row._id || fullName, i);

                  return (
                    <tr key={row.user_id || row._id || i} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold shrink-0">{rankNum}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {row.profile_picture ? (
                            <img src={row.profile_picture} alt={fullName} className="w-8 h-8 rounded-full object-cover shrink-0 border border-slate-200" />
                          ) : (
                            <div className={`w-8 h-8 rounded-full ${avatarColorClass} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                              {initials}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                              {fullName}
                            </div>
                            {row.email && (
                              <div className="text-[11px] text-slate-400 font-normal">{row.email}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-500">{role}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {allCats.length > 0 &&
                            allCats.map((cat, cIdx) => (
                              <span key={cIdx} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">
                                {typeof cat === 'object' ? (cat.name || cat.category_name) : cat}
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${Math.min(100, Math.max(0, effPct))}%` }}></div>
                          </div>
                          <span className="text-xs font-medium text-slate-700 w-8 text-right">{effPct}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <div className="h-1 flex-1 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${Math.min(100, Math.max(0, delPct))}%` }}></div>
                          </div>
                          <span className="text-xs font-medium text-slate-700 w-8 text-right">{delPct}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-base font-bold text-slate-900">{kpiScore}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-sm text-slate-500">
                    No leader ranking data available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Contribution Heatmap */}
      <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm p-6 overflow-hidden">
        <div className="mb-6">
          <h2 className="text-base font-bold text-slate-900 tracking-tight">Contribution heatmap</h2>
          <p className="text-xs text-slate-400 font-medium mt-0.5">KPI score · monthly · last 12 months</p>
        </div>
        
        <div className="overflow-x-auto">
          {heatmapRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-400 font-medium">
              No heatmap data available.
            </div>
          ) : (
            <table className="w-full text-center border-separate" style={{ borderSpacing: '6px 8px' }}>
              <thead>
                <tr>
                  <th className="p-1 text-left w-28"></th>
                  {displayMonths.map((m, idx) => (
                    <th key={idx} className="p-1 text-xs font-medium text-slate-400 w-[72px] text-center">{m}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapRows.map((row) => (
                  <tr key={row.id}>
                    <td className="py-1 pr-1.5 text-left whitespace-nowrap w-28">
                      <div className="flex items-center gap-2">
                        <div className={`w-7 h-7 rounded-full ${row.color} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-2xs`}>
                          {row.init}
                        </div>
                        <span className="text-xs font-semibold text-slate-900">{row.name}</span>
                      </div>
                    </td>
                    {(row.monthlyScores || []).map((mObj, j) => {
                      const score = typeof mObj === 'object' ? (mObj.score !== undefined ? mObj.score : 0) : Number(mObj || 0);
                      const targetVal = (typeof mObj === 'object' && mObj.target_score) ? mObj.target_score : (row.targetScore || 100);
                      const style = getHeatmapColorStyle(score, targetVal);
                      const pctAchieved = Math.min(100, Math.round((score / (targetVal || 100)) * 100));

                      return (
                        <td key={j} className="p-0">
                          <div 
                            className="w-[72px] h-9 flex items-center justify-center rounded-md text-xs font-bold transition-all hover:scale-105 shadow-2xs cursor-default"
                            style={style}
                            title={`${score}/${targetVal} (${pctAchieved}%)`}
                          >
                            {score}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Target Score Opacity Legend (#1a1a18) */}
          <div className="flex justify-end items-center gap-1.5 mt-6 text-xs font-medium">
            <span className="text-xs text-slate-400 font-medium mr-1">Min</span>
            <div className="w-4 h-4 rounded-[4px] border border-slate-200 bg-white" title="0% Target Achieved"></div>
            <div className="w-4 h-4 rounded-[4px]" style={{ backgroundColor: 'rgba(26, 26, 24, 0.25)' }} title="25% Target Achieved"></div>
            <div className="w-4 h-4 rounded-[4px]" style={{ backgroundColor: 'rgba(26, 26, 24, 0.50)' }} title="50% Target Achieved"></div>
            <div className="w-4 h-4 rounded-[4px]" style={{ backgroundColor: 'rgba(26, 26, 24, 0.75)' }} title="75% Target Achieved"></div>
            <div className="w-4 h-4 rounded-[4px]" style={{ backgroundColor: 'rgba(26, 26, 24, 1.0)' }} title="100% Target Achieved"></div>
            <span className="text-xs text-slate-400 font-medium ml-1">Max</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KPIAssessments;
